import { useParams } from 'react-router-dom'
import { EarnAssetDetailsAsset } from '../types'
import {
  EARN_ASSET_DETAILS_STATE,
  EarnAssetDetailsData,
  UseEarnAssetDetailsDataHandlers,
} from '../types/assetDetailsState'
import { formatAPY, formatUsd } from '../utils/utils'
import { useEarnAssetDetailsQuery } from './api/useEarnAssetDetailsQuery'
import { useEarnStatisticsQuery } from './api/useEarnStatisticsQuery'
import { useIsDirectEarning } from './useIsDirectEarning'

export const useEarnAssetDetailsData = ({
  onDeposit,
  onWithdraw,
}: UseEarnAssetDetailsDataHandlers): EarnAssetDetailsData => {
  const { portfolioId } = useParams<{ portfolioId: string }>()
  const { data, isLoading } = useEarnAssetDetailsQuery(portfolioId)
  const { data: statistics, isLoading: isStatisticsLoading } = useEarnStatisticsQuery()
  const isDirectEarning = useIsDirectEarning()

  const common = { isStatisticsLoading, isDirectEarning, portfolioId }

  if (isLoading) {
    return { ...common, assetDetailsState: EARN_ASSET_DETAILS_STATE.LOADING }
  }

  if (!data) {
    return { ...common, assetDetailsState: EARN_ASSET_DETAILS_STATE.NOT_FOUND }
  }

  const merchantStatistics = statistics?.currencies[data.asset]
  const assetWithPortfolio: EarnAssetDetailsAsset = { ...data, portfolioId }

  return {
    ...common,
    assetDetailsState: EARN_ASSET_DETAILS_STATE.READY,
    asset: data.asset,
    walletId: data.walletId,
    epcTakesEarn: data.epcTakesEarn,
    earnRate: formatAPY(data.myEarnRate ?? 0),
    customerEarnRate: formatAPY(data.customerEarnRate ?? 0),
    summary: {
      allocatedFunds: formatUsd(data.fundsOverview.allocatedFunds),
      unallocatedFunds: formatUsd(merchantStatistics?.merchantMovableBalance.amount),
    },
    totalEarnings: {
      allocationEarnings: formatUsd(data.totalEarnings.fromMyAllocation),
      customerEarnings: formatUsd(data.totalEarnings.fromCustomers),
      totalEarnings: formatUsd(data.totalEarnings.total),
    },
    customerSummary: {
      totalCustomerFundsEarning: formatUsd(merchantStatistics?.customerEarningBalance.amount),
    },
    onDeposit: () => onDeposit(assetWithPortfolio),
    onWithdraw: () => onWithdraw(assetWithPortfolio),
  }
}
